import dayjs from 'dayjs';
import { inject } from 'mobx-react';
import { observer } from 'mobx-react-lite';
import React, { useCallback, useEffect, useState } from 'react';
import withAuth from '../../../HOC/withAuth';
import http from '../../../http';
import { formatMoney } from '../../../money';

const C = {
  brand:  '#F777B0',
  ink:    '#1A1614',
  ink2:   '#3D3632',
  ink3:   '#8A8079',
  paper:  '#EDE6D5',
  paper2: '#E2D9C6',
  hair:   '#E8E0D0',
  serif:  '"Instrument Serif", "Songti SC", serif',
  sans:   '"Inter", "Noto Sans SC", -apple-system, sans-serif',
  mono:   'ui-monospace, monospace',
};

function CheckCircle() {
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
      <circle cx="36" cy="36" r="36" fill={C.brand} fillOpacity=".12"/>
      <circle cx="36" cy="36" r="28" fill={C.brand} fillOpacity=".18"/>
      <circle cx="36" cy="36" r="20" fill={C.brand}/>
      <path d="M27 36.5l6 6 12-12" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
    </svg>
  );
}

function WarnCircle() {
  return (
    <svg width="72" height="72" viewBox="0 0 72 72" fill="none">
      <circle cx="36" cy="36" r="36" fill="#F4A261" fillOpacity=".12"/>
      <circle cx="36" cy="36" r="20" fill="#F4A261"/>
      <path d="M36 27v10M36 42v2" stroke="#fff" strokeWidth="2.5" strokeLinecap="round"/>
    </svg>
  );
}

function Row({ label, value, mono }) {
  return (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
      padding: '11px 0', borderBottom: `1px solid ${C.hair}`,
    }}>
      <span style={{ fontFamily: C.mono, fontSize: 11, letterSpacing: 0.8, color: C.ink3, textTransform: 'uppercase' }}>
        {label}
      </span>
      <span style={{ fontFamily: mono ? C.mono : C.sans, fontSize: 13, color: C.ink2, fontVariantNumeric: 'tabular-nums' }}>
        {value}
      </span>
    </div>
  );
}

const SubscriptionCallback = inject()(observer(() => {
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [outTradeNo, setOutTradeNo] = useState(null);

  const checkStatus = useCallback(() => {
    if (!outTradeNo) return;
    http.get(`/user/subscription/query/${outTradeNo}`)
      .then(res => setBooking(res.data.booking))
      .finally(() => setLoading(false));
  }, [outTradeNo]);

  useEffect(() => {
    const no = new URLSearchParams(window.location.search).get('out_trade_no');
    setOutTradeNo(no);
  }, []);

  useEffect(() => { checkStatus(); }, [checkStatus]);

  const success = booking?.status === 'completed';
  const pending = booking?.status === 'pending';

  // Loading
  if (loading) return (
    <div style={{
      position: 'fixed', inset: 0, background: C.paper,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontFamily: C.serif, fontSize: 22, color: C.ink3,
    }}>
      查询中…
    </div>
  );

  // No booking found
  if (!booking) return (
    <div style={{
      position: 'fixed', inset: 0, background: C.paper,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 16,
    }}>
      <span style={{ fontFamily: C.serif, fontSize: 28, color: C.ink }}>未找到订单</span>
      <span style={{ fontFamily: C.sans, fontSize: 14, color: C.ink3 }}>请检查链接或联系客服</span>
      <button onClick={window.close} style={{
        marginTop: 8, padding: '10px 24px', borderRadius: 10, border: `1.5px solid ${C.hair}`,
        background: '#fff', color: C.ink2, fontFamily: C.sans, fontSize: 14, cursor: 'pointer',
      }}>关闭窗口</button>
    </div>
  );

  return (
    <div style={{
      position: 'fixed', inset: 0, background: C.paper,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24, fontFamily: C.sans,
    }}>
      <div style={{ width: '100%', maxWidth: 420 }}>

        {/* Icon + heading */}
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ marginBottom: 20 }}>
            {success ? <CheckCircle /> : <WarnCircle />}
          </div>
          <div style={{
            fontFamily: C.serif, fontSize: 34, color: C.ink,
            letterSpacing: -0.5, marginBottom: 8,
          }}>
            {success ? '订阅成功' : '支付确认中'}
          </div>
          <div style={{ fontSize: 14, color: C.ink3, lineHeight: 1.6 }}>
            {success
              ? `会员有效期至 ${dayjs(booking.endDate).format('YYYY 年 M 月 D 日')}`
              : '支付宝尚未确认到账，稍等片刻后刷新。'}
          </div>
        </div>

        {/* Details card */}
        <div style={{
          background: '#fff', border: `1px solid ${C.hair}`,
          borderRadius: 14, padding: '4px 22px 8px',
          marginBottom: 20,
        }}>
          <Row label="订单号" value={booking.id} mono />
          <Row
            label="订阅周期"
            value={`${dayjs(booking.startDate).format('YYYY-MM-DD')} — ${dayjs(booking.endDate).format('YYYY-MM-DD')}`}
          />
          <Row label="时长" value={`${booking.duration} 个月`} />
          <Row label="金额" value={`¥ ${formatMoney(booking.amount)}`} />
          {booking.trade_no && <Row label="支付宝单号" value={booking.trade_no} mono />}
        </div>

        {/* Actions */}
        <div style={{ display: 'flex', gap: 10 }}>
          {pending && (
            <button onClick={checkStatus} style={{
              flex: 1, height: 46, borderRadius: 12, border: 'none',
              background: C.brand, color: '#fff',
              fontFamily: C.sans, fontSize: 14, fontWeight: 500, cursor: 'pointer',
              boxShadow: '0 8px 24px -8px rgba(247,119,176,.5)',
            }}>
              刷新状态
            </button>
          )}
          <button onClick={window.close} style={{
            flex: 1, height: 46, borderRadius: 12,
            border: `1.5px solid ${C.hair}`,
            background: '#fff', color: C.ink2,
            fontFamily: C.sans, fontSize: 14, fontWeight: 500, cursor: 'pointer',
          }}>
            {success ? '关闭窗口' : '稍后再试'}
          </button>
        </div>

      </div>
    </div>
  );
}));

export default withAuth(SubscriptionCallback);
